import { Fragment, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useQuery } from "@apollo/client";
import { Dictionary } from "lodash";
import {
  OfficeSpecsQueryQuery,
  OfficeSpecsQueryQueryVariables,
} from "@/__generated__/graphql";
import DeliverableRow, { Deliverable } from "./DeliverableRow";
import { Service } from "../SelectSpecs";
import { OFFICE_QUERY_FOR_SPECS } from "@/graphql/queries/common/OfficeQuery";
import { useField } from "formik";

type Spec = Service["specs"][number];
interface Props {
  className?: string;
  labelClassName?: string;
  specOptions: Spec[];
  field: {
    value: Deliverable[];
    name: string;
  };
  projectId: string;
  serviceId: string;
  label?: string;
  onChange: (deliverables: Deliverable[]) => void;
  disabled?: boolean;
  editSpecs?: boolean;
  showEmptyDeliverableRow?: boolean;
}

const keyDeliverable = (deliverable: Deliverable, i: number) => {
  if (deliverable) {
    return `${deliverable.specId}:${deliverable.formatId} ${i}`;
  }
  return i.toString();
};

const Deliverables = ({
  className,
  labelClassName,
  specOptions,
  onChange,
  editSpecs,
  label = "Delivery Specifications",
  showEmptyDeliverableRow = false,
  projectId,
  serviceId,
  disabled = false,
  ...rest
}: Props) => {
  const [field] = useField<Deliverable[]>(rest.field.name);
  const value = field.value ?? [];

  const addEmptyDeliverableRow = () => {
    onChange([...value, {}]);
  };
  const removeEmptyDeliverableRow = () => {
    const currentEmptyRowIndex = value.findIndex(
      (ele) => !Object.keys(ele).length,
    );
    onChange([...value.splice(currentEmptyRowIndex, 1)]);
  };
  const setData = (data: OfficeSpecsQueryQuery) => {
    if (data?.project?.creator.office) {
      const curr_specs = JSON.parse(
        data?.project?.creator?.office?.defaultSpecs as string,
      );
      const current_specs = curr_specs[serviceId];

      if (current_specs) {
        const new_specs = current_specs.map((specs: Dictionary<string>) => {
          const spec = specOptions.find((option) => option.id === specs.specId);
          return {
            ...specs,
            formatName: spec?.formats.find((obj) => obj.id === specs.formatId)
              ?.format,
            specName: spec?.name,
          };
        });
        onChange(new_specs);
      }
    }
  };
  const setDefault = useCallback(setData, []);
  useEffect(() => {
    if (showEmptyDeliverableRow) {
      addEmptyDeliverableRow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const { data } = useQuery<
    OfficeSpecsQueryQuery,
    OfficeSpecsQueryQueryVariables
  >(OFFICE_QUERY_FOR_SPECS, {
    variables: { projectId },
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!data) return;
    setDefault(data);
  }, [data, setDefault]);
  return (
    <Fragment>
      <div className={cn(className, "flex")}>
        {!editSpecs && (
          <span className={cn(labelClassName, "min-w-50 mt-1")}>{label}</span>
        )}

        <div
          className={
            !editSpecs ? "flex flex-col gap-[20px]" : "flex flex-col gap-2"
          }
        >
          {value.map((deliverable, currentIndex) => (
            <DeliverableRow
              key={keyDeliverable(deliverable, currentIndex)}
              deliverable={deliverable}
              setDeliverable={(updatedDeliverable) =>
                onChange(
                  value.map((currentDeliverable, index) =>
                    index === currentIndex
                      ? updatedDeliverable
                      : currentDeliverable,
                  ),
                )
              }
              removeDeliverable={() => {
                onChange(value.filter((_, index) => index !== currentIndex));
              }}
              // filter out the null specs (following the previous file which said they could be null)
              specOptions={specOptions}
              showRemoveButton={currentIndex !== 0}
              addEmptyDeliverableRow={addEmptyDeliverableRow}
              removeEmptyDeliverableRow={removeEmptyDeliverableRow}
              deliverableValue={value}
              projectId={projectId}
              serviceId={serviceId}
              editSpecs={editSpecs}
              disabled={disabled}
            />
          ))}
        </div>
      </div>
    </Fragment>
  );
};

export default Deliverables;
